import React, {useMemo} from 'react';
import {
  Breadcrumb,
  ButtonWithIcon,
  Icon,
  ProgressIndicator
} from '@/components/atoms';
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper';
import {
  PageHeader,
  SaveCancelFooter,
  SectionTitle
} from '@/components/molecules';
import {FileUploadWithModal, PageContainer} from '@/components/organisms';
import {useCreateBulkPointModifiers} from '@/pages/BulkPointModifiers/CreateBulkPointModifiers-hook';
import {getModal} from '@/utils/CommonModal';
import {convertToLowerCase} from '@/utils/commons';
import {COLOR} from '@/utils/constants/colors';
import {
  ACTION_DROPDOWN,
  Constants,
  STORE_SELECTOR_DROPDOWN
} from '@/utils/constants/commonConstants';
import {SIZE} from '@/utils/constants/globalStyle';
import {ROUTES} from '@/utils/constants/routesConstants';
import './_createBulkPointModifiers.scss';

export const CreateBulkPointModifiers = () => {
  const {
    formData,
    setFormData,
    handleFormChange,
    handleFileDownload,
    validateFormData,
    getScheduleInfo,
    bulkModifierSetupJsonSchema,
    bulkModifierSetupUISchema,
    rulesJsonSchema,
    rulesUISchema,
    numOfDaysSchema,
    numOfDaysUISchema,
    numOfPointsSchema,
    numOfPointsUISchema,
    scheduleJsonSchema,
    scheduleUISchema,
    startDateJsonSchema,
    isNameValid,
    handleValidationModalClose,
    handleSaveDraft,
    handleNext,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    handleScheduleChange,
    handleFileDelete,
    openUpload,
    setOpenUpload,
    isCancel,
    navigate
  } = useCreateBulkPointModifiers();

  const isInValidData = validateFormData({...formData});
  const {scheduleRowClass, showStartDate} = getScheduleInfo(
    formData?.schedule?.toLowerCase()
  );

  /* Rendering startDate ui schema for dynamic validation */
  const renderStartDate = useMemo(
    () => (startDate, endDate) => ({
      type: 'HorizontalLayout',
      className: '',
      elements: [
        {
          type: 'HorizontalLayout',
          elements: [
            {
              type: 'Control',
              scope: '#/properties/startDate',
              component: 'datepicker',
              label: 'Start Date*',
              placeholder: '',
              isDark: true,
              required: false,
              dependentDate: endDate,
              validationRules: [
                {
                  type: 'required',
                  msg: ' is required'
                },
                {
                  type: 'startDate',
                  msg: ' cannot be in the past.'
                },
                {
                  type: 'startDate',
                  endDate: endDate
                }
              ]
            }
          ]
        }
      ]
    }),
    []
  );

  const getCustomModal = (type) => {
    switch (type) {
      case 'CANCEL_MODAL':
        return getModal({
          title: 'Are you sure you want to cancel?',
          text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
          showPrimaryButton: true,
          primaryButtonText: 'Yes',
          onClickPrimaryButton: onClickPrimaryButton,
          showSecondaryButton: true,
          secondaryButtonText: 'No',
          onClickSecondaryButton: onClickSecondaryButton,
          showModal: isCancel,
          onCloseModal: handleCancel,
          hideCloseButton: false
        });
      case 'NAME_VALIDATION_MODAL':
        return getModal({
          title: 'Duplicate Name Detected',
          text: (
            <>
              It looks like there's already a Bulk Point Modifier with this
              name.
              <br />
              To avoid confusion, please enter a unique name before continuing.
            </>
          ),
          showPrimaryButton: true,
          primaryButtonText: 'Edit Name',
          onClickPrimaryButton: handleValidationModalClose,
          showSecondaryButton: false,
          showModal: !isNameValid,
          onCloseModal: handleValidationModalClose,
          hideCloseButton: true
        });
      default:
        return null;
    }
  };

  return (
    <div className='flex flex-col'>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: '',
          breadcrumbs: [
            {
              label: '',
              href: ROUTES.HOME
            },
            {
              label: 'Bulk Point Modifiers',
              href: ROUTES.BULK_POINT_MODIFIERS
            },
            {
              label: 'Create Bulk Modifiers',
              href: ROUTES.BULK_POINT_MODIFIERS
            }
          ]
        }}
        id={'cbpm_bradcrumb'}
      />
      <PageHeader title='Create Bulk Point Modifiers' />
      <ProgressIndicator
        currentStep={1}
        completed={false}
        steps={2}
        titles={[{title: 'Setup'}, {title: 'Review'}]}
        onStepClick={(step) => {
          if (step === 2) {
            navigate(ROUTES.BULK_POINT_MODIFIER_REVIEW);
          }
        }}
      />
      <PageContainer data-testid='create-bulk-point-modifiers-content'>
        <div className='createBulkPointModifiersContent'>
          <div className='w-1/2'>
            <SectionTitle label='Bulk Modifier Setup' />
            <div className='flex flex-col gap-4 lg:flex-row'>
              <JsonFormsWrapper
                jsonschema={bulkModifierSetupJsonSchema}
                uischema={bulkModifierSetupUISchema(formData)}
                data={formData}
                onChange={(data) => handleFormChange(data)}
              />
            </div>
          </div>

          <div className='w-1/2'>
            <SectionTitle label='Rules' />
            <div className='flex flex-col gap-4 lg:flex-row'>
              <JsonFormsWrapper
                jsonschema={rulesJsonSchema}
                uischema={rulesUISchema}
                data={formData}
                onChange={(data) => {
                  data.numberOfDays = '';
                  data.numberOfPoints = '';
                  handleFormChange(data);
                }}
              />
              {convertToLowerCase(formData?.actions) ===
                ACTION_DROPDOWN.REMOVE_ALL_EXCEPT && (
                <JsonFormsWrapper
                  jsonschema={numOfDaysSchema}
                  uischema={numOfDaysUISchema}
                  data={formData}
                  onChange={(data) => handleFormChange(data)}
                />
              )}
              {convertToLowerCase(formData?.actions) ===
                ACTION_DROPDOWN.REMOVE_NUM_POINTS && (
                <JsonFormsWrapper
                  jsonschema={numOfPointsSchema}
                  uischema={numOfPointsUISchema}
                  data={formData}
                  onChange={(data) => handleFormChange(data)}
                />
              )}
              {convertToLowerCase(formData?.actions) ===
                ACTION_DROPDOWN.ADD_POINTS && (
                <JsonFormsWrapper
                  jsonschema={numOfPointsSchema}
                  uischema={numOfPointsUISchema}
                  data={formData}
                  onChange={(data) => handleFormChange(data)}
                />
              )}
            </div>
          </div>

          <div className={scheduleRowClass}>
            <SectionTitle label='Availability' />
            <div className='flex flex-col gap-4 lg:flex-row'>
              <JsonFormsWrapper
                jsonschema={scheduleJsonSchema}
                uischema={scheduleUISchema}
                data={formData}
                onChange={handleScheduleChange}
              />
              {showStartDate && (
                <JsonFormsWrapper
                  jsonschema={startDateJsonSchema}
                  uischema={renderStartDate(
                    formData?.startDate,
                    formData?.endDate
                  )}
                  data={formData}
                  onChange={(data) => handleFormChange(data)}
                />
              )}
            </div>
          </div>

          {/* Excel Upload' */}

          {convertToLowerCase(formData?.storeSelector) ===
            STORE_SELECTOR_DROPDOWN.EXCEL_UPLOAD && (
            <div className=''>
              <SectionTitle label='Upload File*' />
              <div className='flex flex-col gap-2 lg:flex-row'>
                <button
                  type='button'
                  className='downLoadIconContainer border-0 bg-transparent p-0'
                  aria-label='Download template'
                  onClick={handleFileDownload}>
                  <Icon icon='file_download' size={SIZE.SMALL} isDark={true} />
                </button>
                {formData?.targetMediaFile?.length === 1 &&
                formData?.targetMediaFile?.[0] ? (
                  <div className='main_upload-title ml-2  flex items-center rounded-lg px-2 py-3.5 text-xs'>
                    <Icon
                      alt='file'
                      className='insert_file_icon'
                      icon='insert_drive_file'
                      size='small'
                    />
                    <p className='w-[105px] truncate pl-2'>
                      {formData?.targetMediaFile?.[0]}
                    </p>
                    <button
                      type='button'
                      className='cach_ed_icon border-0 bg-transparent p-0'
                      aria-label='Remove file'
                      data-testid='file-delete-icon'
                      onClick={handleFileDelete}>
                      <Icon alt='cached' icon='cached' size='small' />
                    </button>
                  </div>
                ) : (
                  <ButtonWithIcon
                    uiSchema={{
                      placeholder: 'Upload Target Template',
                      title: 'Upload Target Template',
                      size: SIZE.SMALL,
                      variant: COLOR.PRIMARY
                    }}
                    iconLeading='upload'
                    handleButton={() => setOpenUpload(true)}
                  />
                )}
              </div>
              <FileUploadWithModal
                title='Upload File'
                fileType='csv'
                uploadFor={Constants.BulkModifiers}
                openUpload={openUpload}
                setOpenUpload={setOpenUpload}
                setFormData={setFormData}
                formData={formData}
                customErrorMessages={{
                  wrongFileType: 'Wrong file type, please use CSV file',
                  wrongFormat:
                    'The format being used is not correct, please use correct file format'
                }}
              />
            </div>
          )}
        </div>
      </PageContainer>
      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleSaveDraft}
        FormError={isInValidData}
      />
      {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
      {isCancel && getCustomModal('CANCEL_MODAL')}
    </div>
  );
};

export default React.memo(CreateBulkPointModifiers);
